// See Documentation: ../../../Document/InsightsMap.md
import mapboxgl from "mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax

import {
    TI_LAYER_IDS,
    TECH_FILTER_PROPS,
    LINE_BUS_PROPS,
    LOAD_PROPS,
    TI_BUS_LAYER_SETTINGS,
    TI_LINE_LAYER_SETTINGS,
    TI_THERMAL_NO_LAYER_SETTINGS,
    TI_THERMAL_WORST_LAYER_SETTINGS,
    TI_LOAD_LAYER_SETTINGS,
    TI_OPERATING_LAYER_SETTINGS,
    TI_RETIRED_LAYER_SETTINGS,
    TI_PLANNED_LAYER_SETTINGS,
} from "../constants/transmissionInsightsConstants";
import {
    generateGeneratorFilterExpression,
    generateLoadFilterExpression,
    generateVoltageFilterExpression,
} from "../../utils/transmission-insights/filter-expression-utils";

import { useTransmissionInsights } from "../../contexts/TransmissionInsightsContext";
import useMapboxSources from "../hooks/useMapboxSources";

import {
    handleBusClick,
    handleLineClick,
    handleBusMouseEnter,
    handleLineMouseEnter,
} from "../../utils/transmission-insights/mouse-event-utils";
import { TIVisibleFeatures, MouseEvent } from "../../types/types";
import Layer, { FilterCategory, Filter } from "../../types/Layer";

import InsightsMap from "./InsightsMap";

type TransmissionInsightsMapProps = {
    mapboxAccessToken: string;
    theme: "light" | "dark";
    isPageExpanded: boolean;
    viewCharts: boolean;
    viewTable: boolean;
    onViewChartsChange: (value: boolean) => void;
    onViewTableChange: (value: boolean) => void;
};

const TransmissionInsightsMap: React.FC<TransmissionInsightsMapProps> = ({
    mapboxAccessToken,
    theme,
    isPageExpanded,
    viewCharts,
    viewTable,
    onViewChartsChange,
    onViewTableChange,
}) => {
    mapboxgl.accessToken = mapboxAccessToken;
    const { updateVisibleFeatures, updateSelectedArea, clearSelectedArea } =
        useTransmissionInsights();
    const { mapboxSource } = useMapboxSources();

    // Step 1. Define new layers and settings
    // helper function to set up the layers
    function initializeTransmissionInsightsLayers(): Layer[] {
        const initialLayers = [
            new FilterCategory({
                id: TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID,
                visible: true,
                label: "Transmission Line",
                subFilters: LINE_BUS_PROPS.map(
                    (entry) =>
                        new Filter({
                            id: "transmission-" + entry.id,
                            visible: true,
                            label: entry.label,
                            color: entry.color,
                            symbolType: "line",
                        })
                ),
                layerSettings: TI_LINE_LAYER_SETTINGS,
                filterProperty: "VOLTAGE",
                generateFilterExpression: generateVoltageFilterExpression,
            }),
            new FilterCategory({
                id: TI_LAYER_IDS.BUS_LOCATION_LAYER_ID,
                visible: true,
                label: "Substations",
                subFilters: LINE_BUS_PROPS.map(
                    (entry) =>
                        new Filter({
                            id: "bus-" + entry.id,
                            visible: true,
                            label: entry.label,
                            color: entry.color,
                            symbolType: "triangle",
                        })
                ),
                layerSettings: TI_BUS_LAYER_SETTINGS,
                filterProperty: "Voltage",
                generateFilterExpression: generateVoltageFilterExpression,
            }),
            new FilterCategory({
                id: "generators",
                visible: true,
                label: "Generators",
                subFilters: [
                    new FilterCategory({
                        id: TI_LAYER_IDS.GENERATOR_OPERATING_LAYER_ID,
                        visible: true,
                        label: "Operating",
                        subFilters: TECH_FILTER_PROPS.map(
                            (filter) =>
                                new Filter({
                                    id: "operating-" + filter.id,
                                    visible: true,
                                    label: filter.label,
                                    symbolType: "other",
                                    icon: filter.icon,
                                })
                        ),
                        layerSettings: TI_OPERATING_LAYER_SETTINGS,
                        filterProperty: "Technology",
                        generateFilterExpression:
                            generateGeneratorFilterExpression,
                    }),
                    new FilterCategory({
                        id: TI_LAYER_IDS.GENERATOR_PLANNED_LAYER_ID,
                        visible: true,
                        label: "Planned",
                        subFilters: TECH_FILTER_PROPS.map(
                            (filter) =>
                                new Filter({
                                    id: "planned-" + filter.id,
                                    visible: true,
                                    label: filter.label,
                                    symbolType: "other",
                                    icon: filter.icon,
                                })
                        ),
                        layerSettings: TI_PLANNED_LAYER_SETTINGS,
                        filterProperty: "Technology",
                        generateFilterExpression:
                            generateGeneratorFilterExpression,
                    }),
                    new FilterCategory({
                        id: TI_LAYER_IDS.GENERATOR_RETIRED_LAYER_ID,
                        visible: true,
                        label: "Retirement",
                        subFilters: TECH_FILTER_PROPS.map(
                            (filter) =>
                                new Filter({
                                    id: "retired-" + filter.id,
                                    visible: true,
                                    label: filter.label,
                                    symbolType: "other",
                                    icon: filter.icon,
                                })
                        ),
                        layerSettings: TI_RETIRED_LAYER_SETTINGS,
                        filterProperty: "Technology",
                        generateFilterExpression:
                            generateGeneratorFilterExpression,
                    }),
                ],
            }),
            new FilterCategory({
                id: TI_LAYER_IDS.LOAD_LAYER_ID,
                visible: true,
                label: "Load",
                subFilters: LOAD_PROPS.map(
                    (entry) =>
                        new Filter({
                            id: entry.id,
                            visible: true,
                            label: entry.label,
                            symbolType: "other",
                        })
                ),
                layerSettings: TI_LOAD_LAYER_SETTINGS,
                generateFilterExpression: generateLoadFilterExpression,
            }),
            new FilterCategory({
                id: "thermal-capacity",
                visible: true,
                label: "Thermal Capacity",
                subFilters: [
                    new Filter({
                        id: TI_LAYER_IDS.THERMAL_CAPACITY_WORST_CLUSTER_LAYER_ID,
                        visible: true,
                        label: "Single Injection",
                        symbolType: "other",
                        layerSettings: TI_THERMAL_WORST_LAYER_SETTINGS,
                    }),
                    new Filter({
                        id: TI_LAYER_IDS.THERMAL_CAPACITY_NO_CLUSTER_LAYER_ID,
                        visible: false,
                        label: "Cluster Injection",
                        symbolType: "other",
                        layerSettings: TI_THERMAL_NO_LAYER_SETTINGS,
                    }),
                ],
            }),
        ];
        initialLayers.forEach((layer) => layer.assignParents());
        return initialLayers;
    }

    const initialLayers: Layer[] = initializeTransmissionInsightsLayers();

    // Step 2. Define layer to visible feature mapping
    const layerIdTypeMap = new Map([
        [TI_LAYER_IDS.BUS_LOCATION_LAYER_ID, TIVisibleFeatures.bus],
        [
            TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID,
            TIVisibleFeatures.transmission,
        ],
        [TI_LAYER_IDS.LOAD_LAYER_ID, TIVisibleFeatures.load],
        [
            TI_LAYER_IDS.THERMAL_CAPACITY_NO_CLUSTER_LAYER_ID,
            TIVisibleFeatures.thermal,
        ],
        [
            TI_LAYER_IDS.THERMAL_CAPACITY_WORST_CLUSTER_LAYER_ID,
            TIVisibleFeatures.thermal,
        ],
        [
            TI_LAYER_IDS.GENERATOR_OPERATING_LAYER_ID,
            TIVisibleFeatures.generator,
        ],
        [TI_LAYER_IDS.GENERATOR_PLANNED_LAYER_ID, TIVisibleFeatures.generator],
        [TI_LAYER_IDS.GENERATOR_RETIRED_LAYER_ID, TIVisibleFeatures.generator],
    ]);

    // Step 3. MOUSE EVENTS
    const mouseHandlers: MouseEvent[] = [
        {
            type: "mouseenter",
            id: TI_LAYER_IDS.BUS_LOCATION_LAYER_ID,
            callback: handleBusMouseEnter,
        },
        {
            type: "mouseenter",
            id: TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID,
            callback: handleLineMouseEnter,
        },
        {
            type: "click",
            id: TI_LAYER_IDS.BUS_LOCATION_LAYER_ID,
            callback: handleBusClick,
        },
        {
            type: "click",
            id: TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID,
            callback: handleLineClick,
        },
    ];

    return (
        <>
            {mapboxSource ? (
                <InsightsMap
                    mapboxAccessToken={mapboxAccessToken}
                    theme={theme}
                    isPageExpanded={isPageExpanded}
                    viewToggleConfig={{
                        viewCharts,
                        viewTable,
                        onViewChartsChange,
                        onViewTableChange,
                    }}
                    updateVisibleFeatures={updateVisibleFeatures}
                    updateSelectedArea={updateSelectedArea}
                    clearSelectedArea={clearSelectedArea}
                    mapboxSource={mapboxSource}
                    config={{
                        mapLayerConfig: initialLayers,
                        mapLayerIds: layerIdTypeMap,
                        mouseHandlers: mouseHandlers,
                    }}
                />
            ) : (
                <p> loading </p>
            )}
        </>
    );
};
export default TransmissionInsightsMap;
